import React from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { GridMoreVertIcon } from '@mui/x-data-grid';

/**
 * Generic per-row actions dropdown for booking rows.
 *
 * The list of available actions is passed in via props so the same widget
 * can be reused across booking views and adapted as more actions ship.
 *
 * Each action descriptor:
 *   {
 *     key: string,                            // unique key
 *     label: string | (row) => string,        // visible label
 *     icon?: ReactNode,                       // optional leading icon
 *     hidden?: (row) => boolean,              // omit from menu entirely
 *     disabledReason?: (row) => string|null,  // non-null => disabled w/ tooltip
 *     onClick: (row) => void | Promise,       // invoked when selected
 *   }
 */
const BookingActionsMenu = ({ row, actions }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const visibleActions = (actions || []).filter(
        (a) => !(typeof a.hidden === 'function' && a.hidden(row))
    );

    if (visibleActions.length === 0) {
        return null;
    }

    const handleOpen = (ev) => {
        ev.stopPropagation();
        setAnchorEl(ev.currentTarget);
    };
    const handleClose = (ev) => {
        ev?.stopPropagation?.();
        setAnchorEl(null);
    };

    const renderItem = (action) => {
        const label = typeof action.label === 'function' ? action.label(row) : action.label;
        const disabledReason = typeof action.disabledReason === 'function'
            ? action.disabledReason(row)
            : null;
        const disabled = Boolean(disabledReason);

        const item = (
            <MenuItem
                key={action.key}
                disabled={disabled}
                onClick={(ev) => {
                    ev.stopPropagation();
                    handleClose();
                    action.onClick(row);
                }}
            >
                {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
                <ListItemText>{label}</ListItemText>
            </MenuItem>
        );

        if (!disabled || !disabledReason) {
            return item;
        }
        // Wrap disabled MenuItems in a span so the Tooltip still triggers.
        return (
            <Tooltip key={action.key} title={disabledReason} placement="left">
                <span>{item}</span>
            </Tooltip>
        );
    };

    return (
        <>
            <IconButton size="small" onClick={handleOpen} aria-label="Booking actions">
                <GridMoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {visibleActions.map(renderItem)}
            </Menu>
        </>
    );
};

export default BookingActionsMenu;
